import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Tooltip,
  Avatar,
  Stack,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  PersonAdd as PersonAddIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { generateClient } from 'aws-amplify/api';
import { listORGANIZATIONS } from 'graphql/queries';
import { listUSERSWithOrganization, listCLIENTSWithOrganizations } from 'graphql/userQueries';
import { updateUSER, deleteUSER } from 'graphql/mutations';
import { userState } from 'redux/userSlice';
import Snackbar from 'components/shared/Snackbar';
import TableLoadingIndicator from 'components/shared/TableLoadingIndicator';
import AuditLogDialog from './AuditLogDialog';
import { logActivity, AUDIT_ACTIONS, formatValueChange } from 'utils/auditLogger';
import AddUserWithOrgDialog from './AddUserWithOrgDialog';
import { listCognitoUsers } from 'services/auth';

const API = generateClient();

// Status colors
const statusColors = {
  ACTIVE: 'success',
  DISABLED: 'error',
  SUSPENDED: 'warning',
  PENDING: 'info'
};

// Role display names
const roleDisplayNames = {
  SystemAdmin: 'System Admin',
  AgencyAdmin: 'Agency Admin',
  ClientAdmin: 'Client Admin',
  ClientViewer: 'Client Viewer',
  ClientPM: 'Project Manager',
  Subcontractor: 'Subcontractor'
};

export default function UserManager() {
  const { loggedInUser } = useSelector(userState);
  
  // State
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [message, setMessage] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null });
  const [userDetailsDrawer, setUserDetailsDrawer] = useState({ open: false, user: null });
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [auditLogDialog, setAuditLogDialog] = useState({ open: false, user: null });
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [organizations, setOrganizations] = useState({});
  const [clients, setClients] = useState({});
  const [agencies, setAgencies] = useState({});
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    role: '',
    organization: '',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // First fetch all organizations
      const orgsResponse = await API.graphql({
        query: listORGANIZATIONS,
        variables: {
          limit: 1000
        }
      });
      
      const allOrgs = orgsResponse?.data?.listORGANIZATIONS?.items || [];
      const orgsMap = {};
      const agenciesMap = {};
      
      allOrgs.forEach(org => {
        if (org && org.id) {
          orgsMap[org.id] = org;
          if (org.type === 'AGENCY' || org.isAgency) {
            agenciesMap[org.id] = org;
          }
        } else {
          console.warn('Invalid organization data:', org);
        }
      });
      
      setOrganizations(orgsMap);
      setAgencies(agenciesMap);
      
      // Fetch Cognito users to get last login information
      let cognitoUsersMap = {};
      try {
        let nextToken = null;
        let allCognitoUsers = [];
        
        // Fetch all Cognito users (paginated)
        do {
          const cognitoResponse = await listCognitoUsers(60, nextToken);
          if (cognitoResponse?.Users) {
            allCognitoUsers = allCognitoUsers.concat(cognitoResponse.Users);
            nextToken = cognitoResponse.NextToken;
          } else {
            nextToken = null;
          }
        } while (nextToken);
        
        // Create a map of email to Cognito user data
        allCognitoUsers.forEach(cognitoUser => {
          if (cognitoUser?.Username) {
            // Extract email from attributes
            const emailAttr = cognitoUser.Attributes?.find(attr => attr.Name === 'email');
            const email = emailAttr?.Value || cognitoUser.Username;

            cognitoUsersMap[email.toLowerCase()] = {
              userCreateDate: cognitoUser.UserCreateDate,
              userLastModifiedDate: cognitoUser.UserLastModifiedDate,
              enabled: cognitoUser.Enabled,
              userStatus: cognitoUser.UserStatus
            };
          }
        });
        
        console.log(`Fetched ${Object.keys(cognitoUsersMap).length} users from Cognito`);
        console.log('Cognito users map:', cognitoUsersMap);
      } catch (cognitoError) {
        console.warn('Failed to fetch Cognito users, last login times will not be available:', cognitoError);
      }
      
      // Fetch all clients to get agency relationships
      const clientsResponse = await API.graphql({
        query: listCLIENTSWithOrganizations,
        variables: {
          limit: 1000
        }
      });
      
      const allClients = clientsResponse?.data?.listCLIENTS?.items || [];
      const clientsMap = {};
      
      allClients.forEach(client => {
        if (client && client.organizationID) {
          clientsMap[client.organizationID] = client;
        } else {
          console.warn('Invalid client data:', client);
        }
      });
      
      setClients(clientsMap);
      
      // Now fetch users with organization details
      // AWS Amplify returns both data and errors when there are GraphQL errors
      const response = await API.graphql({
        query: listUSERSWithOrganization,
        variables: {
          limit: 1000
        }
      });
      
      // Check if response has errors (partial data scenario)
      if (response?.errors && response?.errors.length > 0) {
        console.warn('GraphQL response contains errors, processing partial data:', response.errors);
        // Log specific serialization errors for debugging
        response.errors.forEach((err, index) => {
          if (err.message?.includes("Can't serialize value")) {
            console.warn(`Serialization error ${index}:`, err.message, 'Path:', err.path);
          }
        });
      }
      
      const allUsers = response?.data?.listUSERS?.items || [];
      
      // Filter out null users and users with invalid date fields
      const validUsers = allUsers.filter((user, index) => {
        // Check if user is null or undefined
        if (!user) {
          console.warn(`User at index ${index} is null or undefined, skipping`);
          return false;
        }
        
        // Check if createdAt or updatedAt are invalid
        if (!user.createdAt || user.createdAt === '' || 
            !user.updatedAt || user.updatedAt === '') {
          console.warn(`User at index ${index} has invalid date fields, skipping:`, {
            id: user.id,
            email: user.email,
            createdAt: user.createdAt,
            updatedAt: user.updatedAt
          });
          return false;
        }
        
        // Validate that dates can be parsed
        try {
          new Date(user.createdAt);
          new Date(user.updatedAt);
          return true;
        } catch (error) {
          console.warn(`User at index ${index} has unparseable date fields, skipping:`, {
            id: user.id,
            email: user.email,
            error: error.message
          });
          return false;
        }
      });
      
      // Enhance users with hierarchy information and Cognito data
      const enhancedUsers = validUsers.map(user => {
        // Safeguard against missing user properties
        if (!user || typeof user !== 'object') {
          console.warn('Invalid user object encountered:', user);
          return null;
        }
        
        const userOrg = user.userOrganization || orgsMap[user.organizationID];
        const client = clientsMap[user.organizationID];
        
        let hierarchy = {
          affiliate: null,
          client: null
        };
        
        // Safe navigation for hierarchy building
        if (userOrg && typeof userOrg === 'object') {
          // Check if user's organization is a client
          if (client && client.agencyOrganizationID) {
            // User is in a client organization
            hierarchy.affiliate = orgsMap[client.agencyOrganizationID];
            hierarchy.client = userOrg;
          } else if (userOrg.type === 'AGENCY' || userOrg.isAgency) {
            // User is in an agency/affiliate organization
            hierarchy.affiliate = userOrg;
          } else if (userOrg.type === 'SYSTEM') {
            // System level user
            hierarchy.affiliate = null;
            hierarchy.client = null;
          } else {
            // Client without agency or standalone org
            hierarchy.client = userOrg;
          }
        }
        
        // Get Cognito data for this user
        const cognitoData = cognitoUsersMap[user.email?.toLowerCase()] || {};

        return {
          ...user,
          hierarchy,
          userOrganization: userOrg,
          // Use lastLoginAt from USER table (updated on each login via App.js)
          // Cognito data is used for account creation date and last modified as fallback
          cognitoCreatedAt: cognitoData.userCreateDate || null,
          cognitoLastModifiedDate: cognitoData.userLastModifiedDate || null,
          cognitoEnabled: cognitoData.enabled,
          cognitoStatus: cognitoData.userStatus,
          // Ensure required fields have defaults
          email: user.email || 'unknown@example.com',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          role: user.role || 'Unknown',
          status: user.status || 'UNKNOWN'
        };
      }).filter(user => user !== null); // Remove any null entries from mapping
      
      // Sort by created date (newest first) with safe date handling
      const sortedUsers = enhancedUsers.sort((a, b) => {
        try {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        } catch (error) {
          console.warn('Error sorting users by date:', error);
          return 0;
        }
      });
      
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
      
      // Show warning if we filtered out users due to data issues
      if (validUsers.length < allUsers.length) {
        const filteredCount = allUsers.length - validUsers.length;
        console.warn(`Filtered out ${filteredCount} users due to invalid data`);
        if (validUsers.length > 0) {
          setMessage({
            severity: 'warning',
            text: `Loaded ${validUsers.length} users (${filteredCount} skipped due to data issues)`
          });
        } else {
          setMessage({
            severity: 'error',
            text: 'No valid users could be loaded. Please check data integrity.'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to load users'
      });
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and search
  useEffect(() => {
    let filtered = [...users];
    
    // Apply search with null safety
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => {
        try {
          const email = (user.email || '').toLowerCase();
          const firstName = (user.firstName || '').toLowerCase();
          const lastName = (user.lastName || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.toLowerCase();
          
          return email.includes(query) ||
                 firstName.includes(query) ||
                 lastName.includes(query) ||
                 fullName.includes(query);
        } catch (error) {
          console.warn('Error during search filtering:', error, user);
          return false;
        }
      });
    }
    
    // Apply status filter with null safety
    if (filters.status) {
      filtered = filtered.filter(user => {
        try {
          return user?.status === filters.status;
        } catch (error) {
          console.warn('Error filtering by status:', error, user);
          return false;
        }
      });
    }
    
    // Apply role filter with null safety
    if (filters.role) {
      filtered = filtered.filter(user => {
        try {
          return user?.role === filters.role;
        } catch (error) {
          console.warn('Error filtering by role:', error, user);
          return false;
        }
      });
    }
    
    // Apply organization filter with null safety
    if (filters.organization) {
      filtered = filtered.filter(user => {
        try {
          if (user?.hierarchy?.affiliate?.id === filters.organization) return true;
          if (user?.hierarchy?.client?.id === filters.organization) return true;
          if (user?.organizationID === filters.organization) return true;
          return false;
        } catch (error) {
          console.warn('Error filtering by organization:', error, user);
          return false;
        }
      });
    }
    
    // Apply date filters with safe date handling
    if (filters.dateFrom) {
      filtered = filtered.filter(user => {
        try {
          const userDate = user.createdAt ? new Date(user.createdAt) : null;
          return userDate && userDate >= new Date(filters.dateFrom);
        } catch (error) {
          console.warn('Error filtering user by dateFrom:', error);
          return false;
        }
      });
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(user => {
        try {
          const userDate = user.createdAt ? new Date(user.createdAt) : null;
          return userDate && userDate <= new Date(filters.dateTo);
        } catch (error) {
          console.warn('Error filtering user by dateTo:', error);
          return false;
        }
      });
    }
    
    setFilteredUsers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [searchQuery, filters, users]);

  // Initial load
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle user action menu
  const handleActionMenuOpen = (event, user) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedUser(null);
  };

  // Handle user status toggle with validation
  const handleStatusToggle = async (user) => {
    if (!user || !user.id) {
      console.error('Invalid user for status toggle:', user);
      setMessage({
        severity: 'error',
        text: 'Cannot update status: Invalid user data'
      });
      return;
    }
    const oldStatus = user.status || 'ACTIVE';
    const newStatus = oldStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
    
    try {
      await API.graphql({
        query: updateUSER,
        variables: {
          input: {
            id: user.id,
            status: newStatus
          }
        }
      });
      
      // Log the activity
      await logActivity({
        entity: 'USER',
        entityID: user.id,
        action: newStatus === 'ACTIVE' ? AUDIT_ACTIONS.USER_ENABLED : AUDIT_ACTIONS.USER_DISABLED,
        field: 'status',
        value: formatValueChange(oldStatus, newStatus),
        user: loggedInUser
      });
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, status: newStatus } : u
      ));
      
      setMessage({
        severity: 'success',
        text: `User ${newStatus === 'ACTIVE' ? 'enabled' : 'disabled'} successfully`
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to update user status'
      });
    }
    
    handleActionMenuClose();
  };

  // Handle user deletion with validation
  const handleDeleteUser = async () => {
    const user = confirmDialog.user;
    
    if (!user || !user.id) {
      console.error('Invalid user for deletion:', user);
      setMessage({
        severity: 'error',
        text: 'Cannot delete user: Invalid user data'
      });
      setConfirmDialog({ open: false, action: null, user: null });
      return;
    }
    
    try {
      // Log the activity before deletion
      await logActivity({
        entity: 'USER',
        entityID: user.id,
        action: AUDIT_ACTIONS.USER_DELETED,
        field: 'user',
        value: JSON.stringify({ email: user.email, name: `${user.firstName || ''} ${user.lastName || ''}`.trim() }),
        user: loggedInUser
      });
      
      await API.graphql({
        query: deleteUSER,
        variables: {
          input: {
            id: user.id
          }
        }
      });
      
      // Remove from local state
      setUsers(prev => prev.filter(u => u.id !== user.id));
      
      setMessage({
        severity: 'success',
        text: 'User deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to delete user'
      });
    }
    
    setConfirmDialog({ open: false, action: null, user: null });
  };

  // Handle password reset with validation
  const handlePasswordReset = async (user) => {
    if (!user || !user.id || !user.email) {
      console.error('Invalid user for password reset:', user);
      setMessage({
        severity: 'error',
        text: 'Cannot reset password: Invalid user data'
      });
      return;
    }
    
    // Log the activity
    await logActivity({
      entity: 'USER',
      entityID: user.id,
      action: AUDIT_ACTIONS.PASSWORD_RESET,
      field: 'password',
      value: 'Reset email sent',
      user: loggedInUser
    });
    
    // This would integrate with AWS Cognito to send password reset email
    setMessage({
      severity: 'info',
      text: `Password reset email sent to ${user.email || 'user'}` 
    });
    handleActionMenuClose();
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      setMessage({
        severity: 'warning',
        text: 'No users selected'
      });
      return;
    }
    
    // Implement bulk actions (enable, disable, delete)
    setMessage({
      severity: 'info',
      text: `Bulk ${action} for ${selectedUsers.length} users`
    });
  };

  // Handle user added from dialog
  const handleUserAdded = (newUser) => {
    // Refresh the users list
    fetchUsers();
    
    setMessage({
      severity: 'success',
      text: `Successfully sent invitation to ${newUser.email}`
    });
    
    setAddUserDialog(false);
  };

  // Export users to CSV with error handling
  const handleExport = () => {
    try {
      const csv = [
        ['Email', 'Name', 'Role', 'Organization', 'Status', 'Last Active', 'Created'],
        ...sortedUsers.map(user => {
          try {
            return [
              user?.email || 'unknown',
              `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown',
              roleDisplayNames[user?.role] || user?.role || 'Unknown',
              user?.organization?.name || user?.userOrganization?.name || '',
              user?.status || 'Unknown',
              user?.lastActiveAt ? format(new Date(user.lastActiveAt), 'yyyy-MM-dd HH:mm') :
                user?.lastLoginAt ? format(new Date(user.lastLoginAt), 'yyyy-MM-dd HH:mm') : 'Never',
              user?.createdAt ?
                format(new Date(user.createdAt), 'yyyy-MM-dd') :
                user?.cognitoCreatedAt ?
                  format(new Date(user.cognitoCreatedAt), 'yyyy-MM-dd') :
                  'Unknown'
            ];
          } catch (error) {
            console.warn('Error exporting user:', error, user);
            return ['Error', 'Error', 'Error', 'Error', 'Error', 'Error', 'Error'];
          }
        })
      ].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setMessage({
        severity: 'success',
        text: `Exported ${sortedUsers.length} users to CSV`
      });
    } catch (error) {
      console.error('Error exporting users:', error);
      setMessage({
        severity: 'error',
        text: 'Failed to export users to CSV'
      });
    }
  };

  // Handle page change
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle select all
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(u => u.id);
      setSelectedUsers(newSelected);
    } else {
      setSelectedUsers([]);
    }
  };

  // Toggle individual selection
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Get organization hierarchy display
  const getHierarchyDisplay = (user) => {
    if (!user) return '-';

    // For users with hierarchy data
    if (user.hierarchy) {
      const parts = [];

      if (user.hierarchy.affiliate) {
        parts.push(user.hierarchy.affiliate.name);
      }

      if (user.hierarchy.client) {
        parts.push(user.hierarchy.client.name);
      }

      if (parts.length === 0) {
        // System level user or no organization
        if (user.userOrganization?.type === 'SYSTEM') {
          return 'System';
        }
        return user.userOrganization?.name || '-';
      }

      return parts.join(' → ');
    }

    // Fallback for users without hierarchy data
    if (user.userOrganization) {
      return user.userOrganization.name;
    }

    return '-';
  };

  // Sorting functions
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedUsers = React.useMemo(() => {
    const getComparableValue = (user, property) => {
      switch (property) {
        case 'name':
          return `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase() || 'unnamed user';
        case 'email':
          return (user.email || 'unknown').toLowerCase();
        case 'role':
          return (roleDisplayNames[user.role] || user.role || 'Unknown').toLowerCase();
        case 'hierarchy':
          return getHierarchyDisplay(user).toLowerCase();
        case 'status':
          return (user.status || 'ACTIVE').toLowerCase();
        case 'lastLoginAt':
          return user.lastActiveAt ? new Date(user.lastActiveAt).getTime() :
                 user.lastLoginAt ? new Date(user.lastLoginAt).getTime() :
                 user.cognitoLastModifiedDate ? new Date(user.cognitoLastModifiedDate).getTime() : 0;
        case 'createdAt':
          return user.createdAt ? new Date(user.createdAt).getTime() :
                 user.cognitoCreatedAt ? new Date(user.cognitoCreatedAt).getTime() : 0;
        default:
          return '';
      }
    };

    try {
      const sortableUsers = [...filteredUsers];
      sortableUsers.sort((a, b) => {
        const aValue = getComparableValue(a, orderBy);
        const bValue = getComparableValue(b, orderBy);

        if (aValue < bValue) {
          return order === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      });
      return sortableUsers;
    } catch (error) {
      console.warn('Error sorting users:', error);
      return filteredUsers;
    }
  }, [filteredUsers, order, orderBy]);

  return (
    <>
      <Snackbar message={message} setMessage={setMessage} />
      
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            User Manager
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              startIcon={<PersonAddIcon />}
              variant="contained"
              onClick={() => setAddUserDialog(true)}
            >
              Add User
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={handleExport}
            >
              Export
            </Button>
            <IconButton onClick={fetchUsers}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Manage all users across the platform hierarchy
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6">{users.length}</Typography>
          <Typography variant="body2" color="text.secondary">Total Users</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6" color="success.main">
            {users.filter(u => u.status === 'ACTIVE').length}
          </Typography>
          <Typography variant="body2" color="text.secondary">Active Users</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6" color="error.main">
            {users.filter(u => u.status === 'DISABLED').length}
          </Typography>
          <Typography variant="body2" color="text.secondary">Disabled Users</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h6" color="info.main">
            {users.filter(u => {
              const lastActivity = u.lastActiveAt ? new Date(u.lastActiveAt) :
                                   u.lastLoginAt ? new Date(u.lastLoginAt) : null;
              const daysSinceActivity = lastActivity ?
                (new Date() - lastActivity) / (1000 * 60 * 60 * 24) : Infinity;
              return daysSinceActivity <= 7;
            }).length}
          </Typography>
          <Typography variant="body2" color="text.secondary">Active This Week</Typography>
        </Paper>
      </Stack>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setFilterDrawer(true)}
            variant={Object.values(filters).some(v => v) ? "contained" : "outlined"}
          >
            Filters
            {Object.values(filters).filter(v => v).length > 0 && (
              <Chip 
                label={Object.values(filters).filter(v => v).length} 
                size="small" 
                sx={{ ml: 1 }}
              />
            )}
          </Button>
        </Stack>
      </Paper>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2">
              {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
            </Typography>
            <Button size="small" onClick={() => handleBulkAction('enable')}>
              Enable
            </Button>
            <Button size="small" onClick={() => handleBulkAction('disable')}>
              Disable
            </Button>
            <Button size="small" color="error" onClick={() => handleBulkAction('delete')}>
              Delete
            </Button>
            <Button size="small" variant="text" onClick={() => setSelectedUsers([])}>
              Clear Selection
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < sortedUsers.length}
                  checked={sortedUsers.length > 0 && selectedUsers.length === sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={orderBy === 'hierarchy'}
                  direction={orderBy === 'hierarchy' ? order : 'asc'}
                  onClick={() => handleRequestSort('hierarchy')}
                >
                  Hierarchy
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'lastLoginAt'}
                  direction={orderBy === 'lastLoginAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('lastLoginAt')}
                >
                  Last Active
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableLoadingIndicator colSpan={9} />
            ) : sortedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <TableRow 
                    key={user.id}
                    hover
                    selected={selectedUsers.includes(user.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {user.firstName?.charAt(0) || user.email?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {user.firstName || user.lastName ? 
                              `${user.firstName || ''} ${user.lastName || ''}`.trim() : 
                              'Unnamed User'
                            }
                          </Typography>
                          {user.id === loggedInUser?.id && (
                            <Chip label="You" size="small" sx={{ height: 16 }} />
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={roleDisplayNames[user.role] || user.role || 'Unknown'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getHierarchyDisplay(user)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status || 'ACTIVE'}
                        size="small"
                        color={statusColors[user.status] || 'default'}
                        icon={user.status === 'ACTIVE' ? <CheckCircleIcon /> : <CancelIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.lastActiveAt ?
                          format(new Date(user.lastActiveAt), 'MMM dd, yyyy h:mm a') :
                          user.lastLoginAt ?
                            format(new Date(user.lastLoginAt), 'MMM dd, yyyy h:mm a') :
                            user.cognitoLastModifiedDate ?
                              format(new Date(user.cognitoLastModifiedDate), 'MMM dd, yyyy h:mm a') :
                              'Never'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.createdAt ?
                          format(new Date(user.createdAt), 'MMM dd, yyyy') :
                          user.cognitoCreatedAt ?
                            format(new Date(user.cognitoCreatedAt), 'MMM dd, yyyy') :
                            'Unknown'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small"
                            onClick={() => setUserDetailsDrawer({ open: true, user })}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More Actions">
                          <IconButton 
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, user)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => setUserDetailsDrawer({ open: true, user: selectedUser })}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleStatusToggle(selectedUser)}>
          {selectedUser?.status === 'ACTIVE' ? (
            <>
              <LockIcon fontSize="small" sx={{ mr: 1 }} />
              Disable User
            </>
          ) : (
            <>
              <LockOpenIcon fontSize="small" sx={{ mr: 1 }} />
              Enable User
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handlePasswordReset(selectedUser)}>
          <LockIcon fontSize="small" sx={{ mr: 1 }} />
          Reset Password
        </MenuItem>
        <MenuItem onClick={() => {
          setAuditLogDialog({ open: true, user: selectedUser });
          handleActionMenuClose();
        }}>
          <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
          View Audit Log
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            setConfirmDialog({ open: true, action: 'delete', user: selectedUser });
            handleActionMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, user: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Are you sure you want to delete user <strong>{confirmDialog.user?.email}</strong>?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, user: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawer}
        onClose={() => setFilterDrawer(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filter Users
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="DISABLED">Disabled</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={filters.role}
                label="Role"
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SystemAdmin">System Admin</MenuItem>
                <MenuItem value="AgencyAdmin">Agency Admin</MenuItem>
                <MenuItem value="ClientAdmin">Client Admin</MenuItem>
                <MenuItem value="ClientViewer">Client Viewer</MenuItem>
                <MenuItem value="ClientPM">Project Manager</MenuItem>
                <MenuItem value="Subcontractor">Subcontractor</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Organization</InputLabel>
              <Select
                value={filters.organization}
                label="Organization"
                onChange={(e) => setFilters({ ...filters, organization: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem disabled><em>Affiliates</em></MenuItem>
                {Object.values(agencies).map(org => (
                  <MenuItem key={org.id} value={org.id} sx={{ pl: 3 }}>
                    {org.name}
                  </MenuItem>
                ))}
                <MenuItem disabled><em>Clients</em></MenuItem>
                {Object.values(organizations).filter(org => 
                  org.type === 'CLIENT' || (!org.type && !org.isAgency && org.id !== 'SYSTEM')
                ).map(org => (
                  <MenuItem key={org.id} value={org.id} sx={{ pl: 3 }}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Created From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="Created To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setFilters({
                  status: '',
                  role: '',
                  organization: '',
                  dateFrom: '',
                  dateTo: ''
                });
              }}
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setFilterDrawer(false)}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* User Details Drawer */}
      <Drawer
        anchor="right"
        open={userDetailsDrawer.open}
        onClose={() => setUserDetailsDrawer({ open: false, user: null })}
      >
        {userDetailsDrawer.user && (
          <Box sx={{ width: 400, p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Avatar sx={{ width: 56, height: 56 }}>
                {userDetailsDrawer.user.firstName?.charAt(0) || userDetailsDrawer.user.email?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {userDetailsDrawer.user.firstName || userDetailsDrawer.user.lastName ? 
                    `${userDetailsDrawer.user.firstName || ''} ${userDetailsDrawer.user.lastName || ''}`.trim() : 
                    'Unnamed User'
                  }
                </Typography>
                <Chip
                  label={userDetailsDrawer.user.status || 'ACTIVE'}
                  size="small"
                  color={statusColors[userDetailsDrawer.user.status] || 'default'}
                />
              </Box>
            </Stack>
            
            <Divider sx={{ mb: 2 }} />
            
            <List>
              <ListItem>
                <ListItemText
                  primary="Email"
                  secondary={userDetailsDrawer.user.email}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Role"
                  secondary={roleDisplayNames[userDetailsDrawer.user.role] || userDetailsDrawer.user.role}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Organization Hierarchy"
                  secondary={
                    <Box>
                      {userDetailsDrawer.user?.hierarchy?.affiliate && (
                        <Typography variant="body2">
                          Affiliate: {userDetailsDrawer.user.hierarchy.affiliate.name}
                        </Typography>
                      )}
                      {userDetailsDrawer.user?.hierarchy?.client && (
                        <Typography variant="body2">
                          Client: {userDetailsDrawer.user.hierarchy.client.name}
                        </Typography>
                      )}
                      {!userDetailsDrawer.user?.hierarchy?.affiliate && !userDetailsDrawer.user?.hierarchy?.client && (
                        <Typography variant="body2">
                          {getHierarchyDisplay(userDetailsDrawer.user)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Created"
                  secondary={
                    userDetailsDrawer.user.createdAt ?
                      format(new Date(userDetailsDrawer.user.createdAt), 'PPpp') :
                      userDetailsDrawer.user.cognitoCreatedAt ?
                        format(new Date(userDetailsDrawer.user.cognitoCreatedAt), 'PPpp') :
                        'Unknown'
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Last Active"
                  secondary={
                    userDetailsDrawer.user.lastActiveAt ?
                      format(new Date(userDetailsDrawer.user.lastActiveAt), 'PPpp') :
                      userDetailsDrawer.user.lastLoginAt ?
                        format(new Date(userDetailsDrawer.user.lastLoginAt), 'PPpp') :
                        'Never'
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="User ID"
                  secondary={userDetailsDrawer.user.id}
                />
              </ListItem>
            </List>
            
            <Stack spacing={1} sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={userDetailsDrawer.user.status === 'ACTIVE' ? <LockIcon /> : <LockOpenIcon />}
                onClick={() => {
                  handleStatusToggle(userDetailsDrawer.user);
                  setUserDetailsDrawer({ open: false, user: null });
                }}
              >
                {userDetailsDrawer.user.status === 'ACTIVE' ? 'Disable User' : 'Enable User'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => {
                  handlePasswordReset(userDetailsDrawer.user);
                  setUserDetailsDrawer({ open: false, user: null });
                }}
              >
                Reset Password
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => {
                  setAuditLogDialog({ open: true, user: userDetailsDrawer.user });
                  setUserDetailsDrawer({ open: false, user: null });
                }}
              >
                View Audit Log
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setConfirmDialog({ open: true, action: 'delete', user: userDetailsDrawer.user });
                  setUserDetailsDrawer({ open: false, user: null });
                }}
              >
                Delete User
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* Audit Log Dialog */}
      <AuditLogDialog
        open={auditLogDialog.open}
        onClose={() => setAuditLogDialog({ open: false, user: null })}
        user={auditLogDialog.user}
      />

      {/* Add User Dialog */}
      <AddUserWithOrgDialog
        open={addUserDialog}
        onClose={() => setAddUserDialog(false)}
        organizations={organizations}
        clients={clients}
        loggedInUser={loggedInUser}
        onUserAdded={handleUserAdded}
        loading={loading}
      />
    </>
  );
}